import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Code, ExternalLink, Copy } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const SchemaStatusWidget = ({ schemaMarkup, onRefresh }) => {
  const [copiedId, setCopiedId] = useState(null);

  const schemaStats = {
    total: schemaMarkup?.length || 0,
    valid: schemaMarkup?.filter(s => s?.validation_status === 'valid')?.length || 0,
    warning: schemaMarkup?.filter(s => s?.validation_status === 'warning')?.length || 0,
    invalid: schemaMarkup?.filter(s => s?.validation_status === 'invalid')?.length || 0
  };

  const schemaTypeIcons = {
    LocalBusiness: '🏢',
    Service: '🛠️',
    Review: '⭐',
    FAQ: '❓',
    BreadcrumbList: '🍞',
    Organization: '🏛️'
  };

  const copyToClipboard = async (schemaData, id) => {
    try {
      await navigator.clipboard?.writeText(JSON.stringify(schemaData, null, 2));
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Schema Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: 'Total Schemas', value: schemaStats?.total, color: 'blue', icon: Code },
          { label: 'Valid', value: schemaStats?.valid, color: 'green', icon: CheckCircle },
          { label: 'Warnings', value: schemaStats?.warning, color: 'yellow', icon: AlertTriangle },
          { label: 'Invalid', value: schemaStats?.invalid, color: 'red', icon: XCircle }
        ]?.map((stat, index) => {
          const Icon = stat?.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6">
              <div className={`w-12 h-12 rounded-lg bg-${stat?.color}-100 flex items-center justify-center mb-4`}>
                <Icon className={`w-6 h-6 text-${stat?.color}-600`} />
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">{stat?.label}</h3>
              <p className="text-3xl font-bold text-gray-900">{stat?.value}</p>
            </div>
          );
        })}
      </div>
      {/* Schema Markup List */}
      <div className="space-y-4">
        {schemaMarkup?.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Code className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No schema markup found</p>
            <p className="text-gray-500 text-sm mt-2">Create schema markup using the generator tool</p>
          </div>
        ) : (
          schemaMarkup?.map((schema) => {
            const validationIcon = {
              valid: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
              warning: { icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50' },
              invalid: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
              pending: { icon: AlertTriangle, color: 'text-gray-600', bg: 'bg-gray-50' }
            };

            const validation = validationIcon?.[schema?.validation_status] || validationIcon?.pending;
            const ValidationIcon = validation?.icon;

            return (
              <div key={schema?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="text-4xl">{schemaTypeIcons?.[schema?.schema_type] || '📄'}</div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">{schema?.schema_type}</h3>
                          <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${validation?.bg}`}>
                            <ValidationIcon className={`w-4 h-4 ${validation?.color}`} />
                            <span className={`text-sm font-medium ${validation?.color}`}>
                              {schema?.validation_status}
                            </span>
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2 break-all">{schema?.page_url}</p>
                        
                        {schema?.validation_errors && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-700">{schema?.validation_errors}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => copyToClipboard(schema?.schema_data, schema?.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Copy schema JSON"
                      >
                        <Copy className={`w-5 h-5 ${copiedId === schema?.id ? 'text-green-600' : 'text-gray-600'}`} />
                      </button>
                      
                      <a
                        href={`https://search.google.com/test/rich-results?url=${encodeURIComponent(schema?.page_url)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Test in Google Rich Results"
                      >
                        <ExternalLink className="w-5 h-5 text-gray-600" />
                      </a>
                    </div>
                  </div>

                  {/* Schema Preview */}
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700">
                      View Schema JSON
                    </summary>
                    <pre className="mt-3 p-4 bg-gray-50 rounded-lg text-xs overflow-x-auto">
                      <code className="text-gray-800">
                        {JSON.stringify(schema?.schema_data, null, 2)}
                      </code>
                    </pre>
                  </details>

                  <div className="mt-4 text-xs text-gray-500">
                    Last updated: {new Date(schema.updated_at)?.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      {/* Refresh Button */}
      <div className="flex justify-center">
        <button
          onClick={onRefresh}
          className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          Refresh Schema Status
        </button>
      </div>
    </div>
  );
};

export default SchemaStatusWidget;